/**
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Temporal/PlainDate/dayOfWeek}
 */
export enum TemporalDayOfWeek {
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6,
  SUNDAY = 7,
}

export function getTemporalDayOfWeek(
  zonedDateTime: Temporal.ZonedDateTime,
): TemporalDayOfWeek {
  return zonedDateTime.dayOfWeek as TemporalDayOfWeek;
}

export function instantToDate(instant: Temporal.Instant): Date {
  return new Date(instant.epochMilliseconds);
}

/**
 * Add duration relative to UTC
 */
export function instantAddUtc(
  instant: Temporal.Instant,
  duration: Temporal.Duration,
) {
  return instant.toZonedDateTimeISO("UTC").add(duration).toInstant();
}

/**
 * Subtract duration relative to UTC
 */
export function instantSubtractUtc(
  instant: Temporal.Instant,
  duration: Temporal.Duration | Temporal.DurationLike,
) {
  return instant.toZonedDateTimeISO("UTC").subtract(duration).toInstant();
}

export function dateToCurrentPlainDate(
  date: Date,
  timeZoneId = Temporal.Now.timeZoneId(),
): Temporal.PlainDate {
  return date.toTemporalInstant().toZonedDateTimeISO(timeZoneId).toPlainDate();
}

export function plainDateToCurrentDate(
  plainDate: Temporal.PlainDate,
  timeZoneId = Temporal.Now.timeZoneId(),
): Date {
  return zonedDateTimeToDate(plainDate.toZonedDateTime(timeZoneId));
}

export function plainDateToCurrentInstant(
  plainDate: Temporal.PlainDate,
  timeZoneId: string,
): Temporal.Instant {
  return dateToInstant(plainDateToCurrentDate(plainDate, timeZoneId));
}

export function zonedDateTimeToDate(
  zonedDateTime: Temporal.ZonedDateTime,
): Date {
  return new Date(zonedDateTime.epochMilliseconds);
}

export function dateToCurrentPlainDateTime(
  date: Date,
  timeZoneId = Temporal.Now.timeZoneId(),
): Temporal.PlainDateTime {
  return date
    .toTemporalInstant()
    .toZonedDateTimeISO(timeZoneId)
    .toPlainDateTime();
}

export function dateToInstant(date: Date): Temporal.Instant {
  return Temporal.Instant.fromEpochMilliseconds(date.getTime());
}

export function epochSecondsToInstant(epochSeconds: number): Temporal.Instant {
  return Temporal.Instant.fromEpochMilliseconds(epochSeconds * 1000);
}

export function instantToEpochSeconds(instant: Temporal.Instant): number {
  return Math.floor(instant.epochMilliseconds / 1000);
}

/**
 * Dates
 * @param start - The start date, inclusive
 * @param end - The end date, exclusive
 */
export function* dates(
  start: Temporal.PlainDate,
  end: Temporal.PlainDate,
): Iterable<Temporal.PlainDate> {
  let date = start;
  while (Temporal.PlainDate.compare(date, end) < 0) {
    yield date;
    date = date.add({ days: 1 });
  }
}

/**
 * Months
 * @param start - The start date, inclusive
 * @param end - The end date, exclusive
 */
export function* months(
  start: Temporal.PlainDate,
  end: Temporal.PlainDate,
): Iterable<Temporal.PlainDate> {
  let date = start;
  while (Temporal.PlainDate.compare(date, end) < 0) {
    yield date;
    date = date.add({ months: 1 });
  }
}

/**
 * The `n`-th occurrence of an ISO weekday within a month — e.g. the 2nd
 * Sunday of May. Throws `RangeError` if the n-th occurrence falls outside
 * the month (e.g. the 5th Friday of a non-leap February).
 */
export function nthWeekdayOfMonth(
  year: number,
  month: number,
  weekday: TemporalDayOfWeek,
  n: number,
): Temporal.PlainDate {
  if (n < 1) {
    throw new RangeError(`nthWeekdayOfMonth: n must be >= 1, got ${n}`);
  }
  const firstOfMonth = Temporal.PlainDate.from({ year, month, day: 1 });
  const offsetToFirstWeekday = (weekday - firstOfMonth.dayOfWeek + 7) % 7;
  const day = 1 + offsetToFirstWeekday + (n - 1) * 7;
  if (day > firstOfMonth.daysInMonth) {
    throw new RangeError(
      `nthWeekdayOfMonth: no ${n}-th weekday ${weekday} in ${year}-${String(
        month,
      ).padStart(2, "0")}`,
    );
  }
  return Temporal.PlainDate.from({ year, month, day });
}

// @ts-expect-error unused-export-debt
function plainDateTimeToCurrentDate(
  plainDateTime: Temporal.PlainDateTime,
  timeZoneId = Temporal.Now.timeZoneId(),
): Date {
  return zonedDateTimeToDate(plainDateTime.toZonedDateTime(timeZoneId));
}

export function plainDateTimeToDate(
  plainDateTime: Temporal.PlainDateTime,
): Date {
  return new Date(plainDateTime.toString() + "Z");
}

export function dateToPlainDateTime(date: Date): Temporal.PlainDateTime {
  const isoString = date.toISOString();
  const dezonedString = isoString.substring(0, isoString.length - 1);
  return Temporal.PlainDateTime.from(dezonedString);
}

export function durationToMilliseconds(duration: Temporal.Duration): number {
  return duration.total({ unit: "milliseconds" });
}

export function instantMax(
  first: Temporal.Instant,
  ...rest: Temporal.Instant[]
): Temporal.Instant {
  return rest.reduce(
    (max, instant) =>
      Temporal.Instant.compare(max, instant) < 0 ? instant : max,
    first,
  );
}

export class Timer {
  private readonly start = Temporal.Now.instant();

  elapsed(): Temporal.Duration {
    const now = Temporal.Now.instant();
    return now.since(this.start);
  }
}
